import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, Languages, ChevronRight, BookOpen, Target, Zap } from 'lucide-react';
import { client } from '../api/client';

interface Subject {
  id: number;
  name: string;
  nameCn: string;
  icon?: string;
  color?: string;
}

interface SubjectStats {
  topicCount: number;
  questionCount: number;
}

const subjectIcons: Record<string, React.ReactNode> = {
  'Mathematics': <Calculator className="w-8 h-8" />,
  'English': <Languages className="w-8 h-8" />,
};

const subjectColors: Record<string, { gradient: string; bg: string; text: string }> = {
  'Mathematics': { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', text: 'text-blue-600' },
  'English': { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', text: 'text-emerald-600' },
};

const grades = [
  { year: 7, label: 'Year 7' },
  { year: 8, label: 'Year 8' },
  { year: 9, label: 'Year 9' },
  { year: 10, label: 'Year 10' },
];

export function SubjectsPage() {
  const [searchParams] = useSearchParams();
  const gradeParam = searchParams.get('grade');
  
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [stats, setStats] = useState<Record<number, SubjectStats>>({});
  const [selectedGrade, setSelectedGrade] = useState<number | null>(
    gradeParam ? parseInt(gradeParam) : null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubjects();
  }, []);

  async function loadSubjects() {
    try {
      const res = await client.api.fetch('/api/public/subjects');
      const data = await res.json();
      setSubjects(data.data || []);
      
      // Load stats for each subject
      for (const subject of data.data || []) {
        const statsRes = await client.api.fetch(`/api/public/subjects/${subject.id}/stats`);
        const statsData = await statsRes.json();
        setStats(prev => ({ ...prev, [subject.id]: statsData.data }));
      }
    } catch (error) {
      console.error('Failed to load subjects:', error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            题库中心
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            选择科目和年级，开始你的学习之旅
          </p>
        </motion.div>

        {/* Grade Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          <button
            onClick={() => setSelectedGrade(null)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              selectedGrade === null
                ? 'bg-slate-900 text-white shadow-lg'
                : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
            }`}
          >
            全部年级
          </button>
          {grades.map((grade) => (
            <button
              key={grade.year}
              onClick={() => setSelectedGrade(grade.year)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                selectedGrade === grade.year
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              {grade.label}
            </button>
          ))}
        </motion.div>

        {/* Subject Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {subjects.map((subject, index) => {
            const colors = subjectColors[subject.name] || { gradient: 'from-slate-500 to-slate-600', bg: 'bg-slate-50', text: 'text-slate-600' };
            const icon = subjectIcons[subject.name] || <BookOpen className="w-8 h-8" />;
            const subjectStats = stats[subject.id] || { topicCount: 0, questionCount: 0 };
            
            return (
              <motion.div
                key={subject.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
              >
                <Link
                  to={`/subjects/${subject.name.toLowerCase()}${selectedGrade ? `?grade=${selectedGrade}` : ''}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden bg-white rounded-2xl border border-slate-100 hover:border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300">
                    {/* Header */}
                    <div className={`bg-gradient-to-r ${colors.gradient} p-6 text-white`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                            {icon}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold">{subject.nameCn}</h3>
                            <p className="text-white/80 text-sm">{subject.name}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="p-6">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className={`${colors.bg} rounded-xl p-4`}>
                          <div className={`text-2xl font-bold ${colors.text}`}>
                            {subjectStats.topicCount}
                          </div>
                          <div className="text-sm text-slate-500">知识点</div>
                        </div>
                        <div className={`${colors.bg} rounded-xl p-4`}>
                          <div className={`text-2xl font-bold ${colors.text}`}>
                            {subjectStats.questionCount}
                          </div>
                          <div className="text-sm text-slate-500">练习题</div>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                          <Target className="w-3 h-3" />
                          NAPLAN
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                          <Zap className="w-3 h-3" />
                          VCE预备
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Empty State */}
        {subjects.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">暂无科目</h3>
            <p className="text-slate-500">科目正在准备中，敬请期待</p>
          </div>
        )}
      </div>
    </div>
  );
}
